import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useGetCurrentMemberQuery } from "../store/services/projectMembers";
import { isProjectAccessDenied } from "../utils/isProjectAccessDenied";
import {
	filterByTicketCategoryScope,
	isPermissionGranted,
	resolveTicketCategoryIds,
} from "../utils/projectPermissionHelpers";

export function useProjectPermission() {
	const projectId = useSelector(
		(state) => state.projectDetails?.project?.id,
	);
	const requireTicketApproval = useSelector(
		(state) => state.projectDetails?.project?.requireTicketApproval ?? false,
	);

	const {
		data: currentMember,
		isLoading,
		isFetching,
		isUninitialized,
		isError,
		error,
	} = useGetCurrentMemberQuery(
		{ projectId },
		{ skip: !projectId },
	);

	const isForbidden = isProjectAccessDenied(error);

	const permissions = useMemo(
		() => currentMember?.permissions ?? {},
		[currentMember?.permissions],
	);
	const isProjectAdmin =
		!isError && currentMember?.isProjectAdmin === true;
	const memberLoading =
		!projectId ||
		(!isError && (isLoading || isFetching || isUninitialized));

	// Project admins implicitly get full ticket access - mirror the can()
	// admin short-circuit so the tickets tab/actions are not gated on an
	// explicitly stored level (which defaults to NONE).
	const ticketViewLevel = isProjectAdmin
		? "ALL"
		: (permissions?.tickets?.view ?? "NONE");
	const ticketEditLevel = isProjectAdmin
		? "ALL"
		: (permissions?.tickets?.edit ?? "NONE");

	const ticketCategoryIds = useMemo(
		() => resolveTicketCategoryIds(currentMember, isProjectAdmin),
		[currentMember, isProjectAdmin],
	);

	const ticketCategorySectionVisible =
		ticketCategoryIds === null || ticketCategoryIds.length > 0;

	const can = useCallback(
		(area, action) => {
			if (memberLoading || isError) return false;
			if (isProjectAdmin) return true;
			const value = permissions?.[area]?.[action];
			return isPermissionGranted(value);
		},
		[memberLoading, isError, isProjectAdmin, permissions],
	);

	const filterByCategoryScope = useCallback(
		(items, getCategoryId) =>
			filterByTicketCategoryScope(
				items,
				getCategoryId,
				ticketCategoryIds,
			),
		[ticketCategoryIds],
	);

	return {
		can,
		isProjectAdmin,
		isLoading: memberLoading,
		isError,
		isForbidden,
		ticketViewLevel,
		ticketEditLevel,
		ticketCategoryIds,
		ticketCategorySectionVisible,
		requireTicketApproval,
		currentMember,
		filterByCategoryScope,
	};
}

export default useProjectPermission;
